import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { getPosterTemplate } from '@/components/admin/marketing/posterTemplates'
import type { PosterBrand } from '@/components/admin/marketing/PosterCanvas'
import type { PosterConfig } from '@/types'

/**
 * One definition of a dine-in table tent, shared by the bulk print run and the
 * single-table reprint (F-FE-N100).
 *
 * It lives in its own module because the two callers must not drift: a tent
 * reprinted for table 7 sits in a holder next to thirty-nine printed by the
 * other path, and any difference in label, layout or brand block is visible
 * across the room. Building the config twice is how that difference gets in.
 */

/**
 * How a table is named on the tent and in the QR sheet's heading.
 *
 * `dining_tables.table_number` is free text (VARCHAR(20)) and real data is not
 * all numbers — the demo seed alone carries `T02` and `TAKEOUT`. Prefixing
 * unconditionally prints "Table TAKEOUT" on a laminated card, so the word is
 * added only when the name is bare digits.
 *
 * This deliberately matches what the guest is shown after scanning
 * (storefront CheckoutView). Staff read the tent, guests read the phone; if the
 * two disagree the tent is the one nobody can correct.
 */
export function tableDisplayLabel(tableNumber: string | null | undefined): string {
  const n = (tableNumber ?? '').trim()
  if (!n) return 'Table'
  return /^\d+$/.test(n) ? `Table ${n}` : n
}

/**
 * Build the tent poster for one table.
 *
 * The table name IS the headline: a stack of tents gets sorted by eye at a
 * table, so the one thing that has to carry across a room is which table this
 * belongs on.
 */
export function buildTableTentConfig(
  defaults: PosterConfig,
  tableNumber: string | null | undefined,
  qrTargetUrl: string,
): PosterConfig {
  return {
    ...defaults,
    layout: 'tent',
    headline: tableDisplayLabel(tableNumber),
    qrTargetUrl,
  }
}

/** The `dine_in` poster template's defaults. Stable, so memoised once. */
export function useTableTentDefaults(): PosterConfig {
  return useMemo(() => getPosterTemplate('dine_in').defaults, [])
}

/**
 * The tenant's own brand block — never Bhookly's.
 *
 * Same derivation, from the same settings keys, as the poster studio, so a tent
 * printed here is identical to one printed there. With no logo configured
 * PosterCanvas falls back to a monogram of the tenant's business name, so a
 * restaurant that has set neither a logo nor a name is the only case that
 * prints something generic.
 */
export function useTableTentBrand(): PosterBrand {
  const { data } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const allSettings = data?.data as Record<string, unknown> | undefined

  return useMemo<PosterBrand>(() => {
    const all = allSettings ?? {}
    const businessName =
      (typeof all.receipt_business_name === 'string' && all.receipt_business_name) ||
      (typeof all.restaurant_name === 'string' && all.restaurant_name) ||
      ''
    const logoUrl = (typeof all.receipt_logo_url === 'string' && all.receipt_logo_url) || ''
    return { businessName, logoUrl, brandedQr: all.branded_qr_enabled !== false }
  }, [allSettings])
}
